import { Link } from "react-router-dom";
import { MapPin, CalendarDays, Users, Navigation, ExternalLink, Facebook } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import { formatDateTime, FALLBACK_IMAGE, imageSrc, priceLabel } from "../lib/api";
import { categoryStyle } from "../lib/categories";
import { useLang } from "../context/LanguageContext";

export const EventCard = ({ event, testIdPrefix = "event-card" }) => {
  const { t } = useLang();
  const isExternal = event.ticketing === "external"; // billetter/påmelding hos arrangøren (Facebook e.l.)
  const soldOut = !isExternal && event.remaining <= 0;
  const cat = categoryStyle(event.category);
  return (
    <Link to={`/arrangement/${event.id}`} data-testid={`${testIdPrefix}-${event.id}`} className="block group">
      <Card className="overflow-hidden card-lift h-full border-border bg-card">
        {/* Tynn kategorifarget stripe øverst – gjør listen lettere å skanne */}
        <div className={`h-1.5 ${cat.stripe}`} aria-hidden="true" data-testid={`event-category-stripe-${event.id}`} />
        <div className="relative aspect-[16/9] overflow-hidden bg-muted">
          <img
            src={imageSrc(event.image_url)}
            alt={event.title}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              e.currentTarget.src = FALLBACK_IMAGE;
            }}
          />
          <div className="absolute top-3 left-3 flex gap-2">
            <Badge
              variant="outline"
              className={`gap-1 shadow-sm ${cat.badge}`}
              data-testid={`event-category-badge-${event.id}`}
            >
              <cat.Icon className="w-3 h-3" aria-hidden="true" />
              {t(event.category)}
            </Badge>
          </div>
          <div className="absolute top-3 right-3">
            {isExternal ? (
              <Badge className="bg-card/95 text-foreground border border-border shadow-sm hover:bg-card/95 gap-1" data-testid={`event-external-badge-${event.id}`}>
                {event.external_host === "Facebook" ? <Facebook className="w-3 h-3" aria-hidden="true" /> : <ExternalLink className="w-3 h-3" aria-hidden="true" />}
                {event.external_price_text || (event.external_host === "Facebook" ? "Facebook" : t("Eksternt"))}
              </Badge>
            ) : soldOut ? (
              <Badge className="bg-[hsl(var(--danger))] text-[hsl(var(--danger-foreground))] hover:bg-[hsl(var(--danger))]">{t("Utsolgt")}</Badge>
            ) : (
              <Badge
                className={
                  (event.max_price ?? 0) === 0
                    ? "bg-[hsl(var(--success))] text-[hsl(var(--success-foreground))] hover:bg-[hsl(var(--success))]"
                    : "bg-primary text-primary-foreground hover:bg-primary"
                }
                data-testid={`event-price-badge-${event.id}`}
              >
                {priceLabel(event)}
              </Badge>
            )}
          </div>
        </div>
        <CardContent className="p-5 space-y-3">
          <h3 className="font-heading font-semibold text-lg leading-snug line-clamp-2">{event.title}</h3>
          <div className="space-y-1.5 text-sm text-muted-foreground">
            <p className="flex items-center gap-2">
              <CalendarDays className="w-4 h-4 shrink-0" />
              {formatDateTime(event.date)}
            </p>
            <p className="flex items-center gap-2">
              <MapPin className="w-4 h-4 shrink-0" />
              {[event.venue, event.city || event.poststed].filter(Boolean).join(", ") || t("Sted kommer")}
            </p>
            {isExternal ? (
              <p className="flex items-center gap-2" data-testid={`event-external-note-${event.id}`}>
                <ExternalLink className="w-4 h-4 shrink-0" />
                {event.external_host === "Facebook" ? t("Facebook-arrangement – info og påmelding hos arrangøren") : t("Billetter/påmelding hos arrangøren")}
              </p>
            ) : (
              <p className="flex items-center gap-2">
                <Users className="w-4 h-4 shrink-0" />
                {soldOut ? t("Ingen ledige plasser") : t("{n} ledige plasser", { n: event.remaining })}
              </p>
            )}
            {event.distance_km != null && (
              <p className="flex items-center gap-2 text-foreground font-medium" data-testid={`event-distance-${event.id}`}>
                <Navigation className="w-4 h-4 shrink-0" />
                {t("Ca. {km} km unna", { km: String(event.distance_km).replace(".", ",") })}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
